import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Megaphone } from "@phosphor-icons/react";
import { dmYhm } from "@/lib/dateFmt";
import { useAuth } from "@/context/AuthContext";

// v1.28.60 — Blocking popup that shows any un-acknowledged broadcast for the
// logged-in user. Cannot be dismissed without clicking "I Acknowledge".
//
// Delivery hardening for mobile (Safari iOS / Chrome Android):
//   - Poll every 15 s (was 30 s) so a new broadcast lands within seconds.
//   - Re-check on window focus AND on visibilitychange -> "visible". Mobile
//     browsers throttle setInterval when the tab is backgrounded; the
//     focus/visibility triggers guarantee we catch a broadcast the moment
//     the user returns to the app.
//   - Modal is scrollable and capped to the visible viewport height (dvh)
//     so long messages on short iPhones don't push the Acknowledge button
//     below the fold.
export default function BroadcastGate() {
    const { user } = useAuth();
    const [pending, setPending] = useState([]);
    const [ack, setAck] = useState(false);

    const load = useCallback(async () => {
        if (!user) return;
        try {
            const r = await api.get("/broadcasts/me/pending");
            setPending(r.data || []);
        } catch { /* silent — 401 on logout etc. */ }
    }, [user]);

    useEffect(() => {
        load();
        const id = setInterval(load, 15 * 1000);
        const onFocus = () => load();
        const onVis = () => { if (document.visibilityState === "visible") load(); };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVis);
        return () => {
            clearInterval(id);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [load]);

    const current = pending[0];
    if (!current || !user) return null;

    const acknowledge = async () => {
        setAck(true);
        try {
            await api.post(`/broadcasts/${current.id}/acknowledge`);
            setPending((cur) => cur.filter((b) => b.id !== current.id));
        } catch { /* keep popup open */ }
        finally { setAck(false); }
    };

    // Blocking overlay — no close button, no backdrop click dismiss.
    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/60 p-3 sm:p-4"
            data-testid="broadcast-gate"
            role="dialog"
            aria-modal="true"
            style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))", paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
            <div
                className="flex w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
                style={{ maxHeight: "calc(100dvh - 1.5rem)" }}
            >
                <div className="flex shrink-0 items-center gap-2 border-b border-amber-200 bg-amber-50 px-5 py-3">
                    <Megaphone size={18} weight="fill" className="text-amber-700" />
                    <div className="text-sm font-bold uppercase tracking-wider text-amber-900">Important Announcement</div>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-5">
                    <div className="text-xs text-slate-500">
                        From {current.created_by_name || "Admin"} · {dmYhm(current.created_at)}
                    </div>
                    <div className="mt-3 whitespace-pre-wrap text-base text-slate-900" data-testid="broadcast-gate-message">
                        {current.message}
                    </div>
                    {current.hindi_message && (
                        <div
                            className="mt-3 whitespace-pre-wrap border-t border-slate-100 pt-3 text-sm text-slate-800"
                            style={{ fontFamily: "'Noto Sans Devanagari', system-ui, sans-serif" }}
                            data-testid="broadcast-gate-hindi"
                        >
                            {current.hindi_message}
                        </div>
                    )}
                    {pending.length > 1 && (
                        <div className="mt-4 rounded-md bg-slate-100 px-2 py-1 text-[11px] text-slate-600">
                            {pending.length - 1} more message{pending.length - 1 === 1 ? "" : "s"} after this
                        </div>
                    )}
                </div>
                <div className="flex shrink-0 justify-end border-t border-slate-100 px-5 py-3">
                    <button
                        type="button"
                        onClick={acknowledge}
                        disabled={ack}
                        data-testid="broadcast-gate-ack-btn"
                        className="inline-flex min-h-[44px] items-center gap-2 rounded-md bg-amber-600 px-6 py-2 text-sm font-bold text-white shadow-sm hover:bg-amber-700 disabled:opacity-60"
                    >
                        {ack ? "Saving…" : "I Acknowledge"}
                    </button>
                </div>
            </div>
        </div>
    );
}
